/**
 * Wall-clock time helpers.
 *
 * All planning dates are "local wall-clock" values without a time zone, exactly
 * like MS Project / ProjectLibre store them (MSPDI: 2027-01-11T08:00:00). We
 * represent them as LocalMinutes: whole minutes since 1970-01-01T00:00 on a
 * clock that has no DST. Arithmetic is therefore plain integer arithmetic.
 * The real-world zone (Europe/Amsterdam) only matters at the edges: "now",
 * Outlook events and ICS feeds.
 */

export type LocalMinutes = number;
/** Day number since 1970-01-01 (day 0 was a Thursday). */
export type DayIndex = number;

export const MINUTES_PER_DAY = 1440;
export const PLANNING_TIME_ZONE = 'Europe/Amsterdam';
/** Windows zone name expected by Microsoft Graph for Europe/Amsterdam. */
export const GRAPH_TIME_ZONE = 'W. Europe Standard Time';

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?$/;

/** Parse "YYYY-MM-DD", "YYYY-MM-DDTHH:mm" or "YYYY-MM-DDTHH:mm:ss" (no zone). */
export function parseLocal(iso: string): LocalMinutes {
  const m = ISO_RE.exec(iso.trim());
  if (!m) throw new Error(`Invalid local date-time: ${iso}`);
  const ms = Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, m[4] ? +m[4] : 0, m[5] ? +m[5] : 0, m[6] ? +m[6] : 0);
  return Math.round(ms / 60000);
}

export function tryParseLocal(iso: string | null | undefined): LocalMinutes | null {
  if (!iso) return null;
  try {
    return parseLocal(iso);
  } catch {
    return null;
  }
}

function pad(n: number, w = 2): string {
  return String(n).padStart(w, '0');
}

/** Format as "YYYY-MM-DDTHH:mm:ss" (MSPDI style). */
export function formatLocal(t: LocalMinutes): string {
  const d = new Date(t * 60000);
  return `${pad(d.getUTCFullYear(), 4)}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:00`;
}

/** Format as "YYYY-MM-DD". */
export function formatDate(t: LocalMinutes): string {
  return formatLocal(t).slice(0, 10);
}

export function dayOf(t: LocalMinutes): DayIndex {
  return Math.floor(t / MINUTES_PER_DAY);
}

export function startOfDay(day: DayIndex): LocalMinutes {
  return day * MINUTES_PER_DAY;
}

export function minuteOfDay(t: LocalMinutes): number {
  return ((t % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
}

/** 0 = Sunday ... 6 = Saturday (same numbering as JS getDay; MSPDI DayType = this + 1). */
export function weekdayOfDay(day: DayIndex): number {
  return (((day + 4) % 7) + 7) % 7;
}

export function weekdayOf(t: LocalMinutes): number {
  return weekdayOfDay(dayOf(t));
}

export function dayFromIsoDate(isoDate: string): DayIndex {
  return dayOf(parseLocal(isoDate.slice(0, 10)));
}

export function isoDateOfDay(day: DayIndex): string {
  return formatDate(startOfDay(day));
}

/** Monday-based ISO week start for a day. */
export function mondayOf(day: DayIndex): DayIndex {
  const wd = weekdayOfDay(day); // 0=Sun
  return day - ((wd + 6) % 7);
}

/** ISO-8601 week number and week-year. */
export function isoWeek(day: DayIndex): { year: number; week: number } {
  const thursday = mondayOf(day) + 3;
  const d = new Date(startOfDay(thursday) * 60000);
  const year = d.getUTCFullYear();
  const jan1 = dayOf(Date.UTC(year, 0, 1) / 60000);
  return { year, week: Math.floor((thursday - jan1) / 7) + 1 };
}

/**
 * Parse an MSPDI/ISO-8601 duration such as "PT8H0M0S", "PT10H24M0S",
 * "P2DT3H" or "PT0H0M0S" into whole minutes. Days count as 24h here (MSPDI
 * working durations are always expressed in hours). A fraction of a minute is
 * truncated toward zero, like ProjectLibre does (PT2H38M36S -> 158).
 */
export function parseIsoDuration(s: string): number {
  const m = /^(-)?P(?:(\d+(?:\.\d+)?)Y)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)W)?(?:(\d+(?:\.\d+)?)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/.exec(s.trim());
  if (!m) throw new Error(`Invalid ISO duration: ${s}`);
  const num = (x: string | undefined) => (x ? parseFloat(x) : 0);
  const minutes =
    num(m[2]) * 365 * 1440 +
    num(m[3]) * 30 * 1440 +
    num(m[4]) * 7 * 1440 +
    num(m[5]) * 1440 +
    num(m[6]) * 60 +
    num(m[7]) +
    num(m[8]) / 60;
  // The tolerance absorbs binary floating-point error (e.g. 4.35 h = 260.99999999999997 min).
  const whole = Math.floor(minutes + 1e-6);
  return m[1] && whole ? -whole : whole;
}

/** Format minutes as MSPDI duration "PT{h}H{m}M0S". */
export function formatIsoDuration(minutes: number): string {
  const neg = minutes < 0;
  const abs = Math.abs(Math.round(minutes));
  return `${neg ? '-' : ''}PT${Math.floor(abs / 60)}H${abs % 60}M0S`;
}

/** Parse "HH:mm[:ss]" to minutes after midnight ("24:00:00" -> 1440). */
export function parseTimeOfDay(s: string): number {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(s.trim());
  if (!m) throw new Error(`Invalid time of day: ${s}`);
  return +m[1]! * 60 + +m[2]!;
}

/** Format minutes after midnight as "HH:mm:ss". 1440 -> "24:00:00". */
export function formatTimeOfDay(minutes: number): string {
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}:00`;
}

/** Current wall-clock time in the planning time zone. */
export function nowLocal(timeZone: string = PLANNING_TIME_ZONE, at: Date = new Date()): LocalMinutes {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  return parseLocal(`${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`);
}

/** Convert a wall-clock value in `timeZone` to a real instant (handles DST). */
export function localToInstant(t: LocalMinutes, timeZone: string = PLANNING_TIME_ZONE): Date {
  // Start from the naive UTC guess and correct by the zone offset (two passes for DST edges).
  let guess = t * 60000;
  for (let i = 0; i < 2; i++) {
    const asLocal = nowLocal(timeZone, new Date(guess));
    guess += (t - asLocal) * 60000;
  }
  return new Date(guess);
}
